import React from 'react';
import { BPCategory } from '@/types/record';
import { classifyBP, getCategoryMeta } from '@/utils/bpClassification';

export type StampVariant =
  | 'normal'
  | 'elevated'
  | 'stage1'
  | 'stage2'
  | 'crisis'
  | 'navy'
  | 'neutral';

export type StampSize = 'sm' | 'md' | 'lg';

export interface StampBadgeProps {
  category?: BPCategory;
  variant?: StampVariant;
  systolic?: number;
  diastolic?: number;
  label?: string;
  size?: StampSize;
  skew?: boolean;
  borderedDouble?: boolean;
  className?: string;
  showTooltip?: boolean;
  onClick?: () => void;
}

export const StampBadge: React.FC<StampBadgeProps> = ({
  category,
  variant,
  systolic,
  diastolic,
  label,
  size = 'md',
  skew = true,
  borderedDouble = false,
  className = '',
  showTooltip = true,
  onClick,
}) => {
  // Determine category either directly or via systolic/diastolic calculation
  let activeCategory: BPCategory = 'NORMAL';
  if (systolic !== undefined && diastolic !== undefined) {
    activeCategory = classifyBP(systolic, diastolic).category;
  } else if (category) {
    activeCategory = category;
  }

  const meta = getCategoryMeta(activeCategory);

  // Variant color mappings
  const variantStyles: Record<StampVariant, { bg: string; border: string; text: string }> = {
    normal: {
      bg: 'bg-emerald-50/90',
      border: 'border-emerald-700',
      text: 'text-emerald-900',
    },
    elevated: {
      bg: 'bg-amber-50/90',
      border: 'border-amber-600',
      text: 'text-amber-900',
    },
    stage1: {
      bg: 'bg-orange-50/90',
      border: 'border-orange-600',
      text: 'text-orange-950',
    },
    stage2: {
      bg: 'bg-red-50/90',
      border: 'border-red-700',
      text: 'text-red-900',
    },
    crisis: {
      bg: 'bg-red-950 text-red-100',
      border: 'border-red-900',
      text: 'text-red-100',
    },
    navy: {
      bg: 'bg-blue-50/90',
      border: 'border-blue-800',
      text: 'text-blue-950',
    },
    neutral: {
      bg: 'bg-paper-200',
      border: 'border-ink-500',
      text: 'text-ink-800',
    },
  };

  const activeStyle = variant
    ? variantStyles[variant]
    : variantStyles[activeCategory.toLowerCase() as StampVariant] || variantStyles.normal;

  // Size specifications
  const sizeStyles: Record<StampSize, { container: string; text: string; dot: string }> = {
    sm: {
      container: 'px-1.5 py-0.2 border-[1.2px] text-[11px] gap-1',
      text: 'tracking-tight',
      dot: 'w-1.5 h-1.5',
    },
    md: {
      container: 'px-2.5 py-0.5 border-[1.5px] text-xs gap-1.5',
      text: 'tracking-wider',
      dot: 'w-2 h-2',
    },
    lg: {
      container: 'px-3.5 py-1 border-2 text-sm gap-2',
      text: 'tracking-widest',
      dot: 'w-2.5 h-2.5',
    },
  };

  const displayLabel = label || meta.labelZh;
  const tooltipText = showTooltip ? `${meta.labelZh} (${meta.labelEn}): ${meta.description}` : undefined;

  return (
    <span
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      title={tooltipText}
      className={`
        inline-flex items-center justify-center select-none
        font-serif font-bold uppercase rounded-xs transition-transform duration-150
        ${activeStyle.bg} ${activeStyle.border} ${activeStyle.text}
        ${sizeStyles[size].container} ${sizeStyles[size].text}
        ${skew ? 'rotate-[-0.8deg] hover:rotate-0' : ''}
        ${borderedDouble ? 'outline outline-1 outline-offset-1' : ''}
        ${onClick ? 'cursor-pointer hover:scale-105 active:scale-95' : ''}
        shadow-[1px_1px_0px_0px_rgba(28,25,23,0.08)]
        ${className}
      `}
    >
      <span className="truncate">{displayLabel}</span>
    </span>
  );
};
